<script setup>
import { isEqualStr, toAmPm, toCanonical24 } from '../shared/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    timezone: String,
    oh: { type: Array, default: [] },
    dayparts: { type: Array, required: false, default: undefined },
    timeFormat: { type: String, required: false, default: 'ampm' }
});

const { t, te } = useI18n();

let today = new Intl.DateTimeFormat('en-US', { weekday: "long" }).format(new Date());

const dayparts = computed(() => Array.isArray(props.dayparts) ? props.dayparts : [])
const dayNameToKey = {
    Monday  : 'mon', Tuesday: 'tue', Wednesday: 'wed', Thursday: 'thu', Friday : 'fri', Saturday : 'sat', Sunday: 'sun',
}

const keyFromName = (name) => dayNameToKey[name] ?? (name || '').slice(0, 3).toLowerCase();

// Localized DISPLAY name for a server day name. Comparisons keep using the raw
// English day.name (see isEqualStr calls); only rendering is translated, so the
// "is today" matching never breaks. Falls back to the raw name if unknown.
const dayLabel = (name) => {
    const key = `weekdays.${keyFromName(name)}`;
    return te(key) ? t(key) : name;
};

const fmt = (t) => (props.timeFormat === 'ampm' ? toAmPm(t) : toCanonical24(t))

// предраскладываем dayparts по дням, с учётом show
const daypartsByDay = computed(() => {
    const map = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] }
    for (const dp of dayparts.value) {
        // show === false -> скрыть
        if (dp && dp.show === false) continue

        const targets = (!dp || !Array.isArray(dp.days) || dp.days.length === 0)
            ? Object.values(dayNameToKey) // для всех дней
            : dp.days.map(d => (d || '').toLowerCase())

        for (const k of targets) {
            if (map[k]) map[k].push(dp)
        }
    }
    return map
})

const getVisibleDayparts = (dayName, range) => {
    const k = keyFromName(dayName)
    let list = daypartsByDay.value[k] || []

    // если нужно учитывать пересечение по времени — включите props.onlyIntersect = true
    if (props.onlyIntersect && range) {
        const rFrom = range.from, rTo = range.to
        list = list.filter(dp => rangesIntersect(dp.from, dp.to, rFrom, rTo))
    }

    return list
}

const openedNow = function (fromTime, toTime, weekday) {
    let fromParts = fromTime.split(":");
    let toParts = toTime.split(":");

    let now = new Date();
    let today = new Intl.DateTimeFormat('en-US', { weekday: "long" }).format(now);
    if (isEqualStr(weekday, today) &&
        typeof fromParts[0] !== 'undefined' &&
        typeof fromParts[1] !== 'undefined' &&
        typeof toParts[0] !== 'undefined' &&
        typeof toParts[1] !== 'undefined'
    ) {
        let shiftStart = new Date();      // work shift end
        shiftStart.setHours(fromParts[0], fromParts[1], 0, 0);
        let shiftEnd = new Date();        // work shift start
        shiftEnd.setHours(toParts[0], toParts[1], 0, 0);

        if (shiftStart <= now && now <= shiftEnd) {
            return true;
        }
    }
    return false;
}
</script>

<template>
    <div class="ezcl-hours">
        <div class="title">{{ t('hours.title') }}</div>
        <div class="schedule">
            <div v-for="(day, dIdx) in props.oh" :class="['day', { 'day--single': day.range.length <= 1 }]">
                <div :class="['ezcl-hours__name', { 'ezcl-hours__name--current': isEqualStr(today, day.name) }]">{{
                    dayLabel(day.name) }}</div>
                <div class="ezcl-hours__info">
                    <div v-if="day.status < 1" class="hours">
                        <div :class="['hoursChip', { 'hoursChip--closed': isEqualStr(today, day.name) }]">{{ t('hours.closed') }}</div>
                    </div>
                    <div v-else class="hours">
                        <div v-for="range in day.range"
                            :class="['hoursChip', { 'hoursChip--current': openedNow(range.from, range.to, day.name) }]">
                            {{ fmt(range.from) }} - {{ fmt(range.to) }}
                        </div>
                    </div>
                    <div class="dayparts" v-show="isEqualStr(today, day.name)">
                        <div v-for="(dp, pIdx) in daypartsByDay[keyFromName(day.name)]" :key="`p-${dIdx}-${pIdx}-${dp?.name}`"
                            class="daypartChip" :title="`${dp.from} - ${dp.to}`">
                            <span class="daypartChip__name">{{ dp.name }}</span>
                            <span class="daypartChip__time">{{ fmt(dp.from) }}–{{ fmt(dp.to) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
.ezcl-hours {
    background: #fff;
    width: 100%;
    min-height: 100px;
    padding: 0.75rem;

    @media (min-width: 768px) {
        padding: 1rem;
    }

    .title {
        font-weight: 700;
        font-size: 1.5em;
        line-height: 1.6;
        margin: -0.5rem 0 0.75rem;
        color: #2f3843;
    }

    .day {
        position: relative;
        display: grid;
        padding: 0.5rem 0;
        align-items: baseline;
        font-size: 0.875em;
        line-height: 1;
        min-height: 2.625rem;
        grid-template-columns: 96px 1fr;
        
        @media (min-width: 414px) {
            grid-template-columns: 120px 1fr;
        }
        @media (min-width: 768px) {
            grid-template-columns: 1fr 1fr;
        }

        &:not(:last-child) {
            border-bottom: 1px solid #f1f5f9;
            box-shadow: 0px 1px 0px #f1f5f963;
        }

        &--single {
            align-items: center;
        }
    }

    &__name {
        letter-spacing: 0.25px;
        color: #4a5565;

        &--current {
            font-weight: 500;
            text-transform: uppercase;
            color: #212121;
        }
    }

    .hours {
        display: grid;
        gap: 0.25rem;
    }

    .hoursRow {
        margin-bottom: 0.125rem;

        &--current {
            font-weight: 700;
        }
    }

    .hoursChip {
        display: inline-block;
        font-size: 0.875em;
        padding: 0.25rem 0.625rem;
        border-radius: 999px;
        color: #334155;
        border: 1px solid #e2e8f0;
        margin-right: auto;

        &--current {
            background: rgb(220 252 231);
            font-weight: 500;
            color: rgb(21 128 61);
            border: 1px solid #bce8b5;
        }

        &--closed {
            color: rgb(194 65 12);
            background-color: rgb(255 237 213);
            border: 1px solid rgb(255, 204, 183);
        }
    }
}

.dayparts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0.375rem;
    margin-left: 1rem;
    flex-direction: column;
}

.daypartChip {
    font-size: 0.75em;
    grid-template-columns: 1fr 1fr;
    gap: 0.375rem;
    display: grid;
    // padding: 3px 8px;
    // border-radius: 4rem;
    // border: 1px dashed #bbb;
    // margin-right: auto;
}

.daypartChip__name {
    font-weight: 600;
    // margin-right: 0.375rem;
}

.daypartChip__time {
    opacity: 0.75;
}
</style>